import { AlertBox, Box, Button, FlexBox, Icon, Typography, getTokenCssVar } from '@vp/swan';
import { __ } from '@99designs/i18n';
import type { ChatErrorCode } from '../../client/types';

export type ChatErrorBannerProps = {
    /** The contract code behind the failure, or null for a network/transport error. */
    errorCode: ChatErrorCode | null;
    /** Whether the retry budget is spent, decided by useChat. */
    retriesExhausted: boolean;
    onRetry: () => void;
    /** Escape hatch offered once retrying has stopped helping. */
    onReview?: () => void;
};

type ErrorPresentation = {
    message: string;
    /** Whether retrying the same request could plausibly succeed. */
    retryable: boolean;
};

/**
 * Copy and retryability for every code in the contract.
 *
 * A Record keyed by the union rather than a switch with a default, so a new error code
 * upstream fails the build here instead of quietly inheriting generic copy. Built inside
 * a function because `__()` must run at render time to pick up the i18n context.
 */
function presentationFor(errorCode: ChatErrorCode | null): ErrorPresentation {
    if (errorCode === null) {
        return {
            message: __("We couldn't send your message — check your connection"),
            retryable: true,
        };
    }

    // Which internal stage broke — the model, the write-back, or the service itself — is our
    // problem, not something the customer can act on differently, so every 5xx shares this.
    const serverFailure: ErrorPresentation = {
        message: __("We couldn't send your message. Please try again."),
        retryable: true,
    };

    const presentations: Record<ChatErrorCode, ErrorPresentation> = {
        conversation_full: {
            message: __("You've reached the message limit for this chat"),
            retryable: false,
        },
        brief_not_draft: {
            message: __('This brief is no longer accepting messages'),
            retryable: false,
        },
        unauthorized: {
            message: __('Your session has expired — refresh the page to continue'),
            retryable: false,
        },
        forbidden: {
            message: __("You don't have access to this brief"),
            retryable: false,
        },
        not_found: {
            message: __('This chat is no longer available'),
            retryable: false,
        },
        // Both indicate the client sent something the service rejected outright.
        // Retrying byte-identical content cannot change the outcome.
        invalid_request: {
            message: __("We couldn't send your message"),
            retryable: false,
        },
        invalid_json: {
            message: __("We couldn't send your message"),
            retryable: false,
        },
        conversation_stale: {
            message: __('Your chat is out of sync — refresh the page to catch up'),
            retryable: false,
        },
        generation_failed: serverFailure,
        write_back_failed: serverFailure,
        service_unavailable: serverFailure,
        internal_error: serverFailure,
        // Deliberately no countdown from the Retry-After header: it reports the whole
        // rate-limit window, not the time actually left, so naming a number would look
        // precise while overstating the wait.
        generation_rate_limited: {
            message: __("You've sent a lot of messages quickly — wait a moment and try again"),
            retryable: true,
        },
    };

    return presentations[errorCode];
}

export function ChatErrorBanner({
    errorCode,
    retriesExhausted,
    onRetry,
    onReview,
}: ChatErrorBannerProps) {
    const { message, retryable } = presentationFor(errorCode);
    const canRetry = retryable && !retriesExhausted;
    const canReview = retryable && retriesExhausted && onReview !== undefined;

    // Repeated failure of a retryable error needs different copy: offering nothing after
    // "try again" would leave the customer with a dead end and no explanation.
    const text =
        retryable && retriesExhausted
            ? __("We're still having trouble sending your message.")
            : message;

    return (
        <Box
            style={{
                width: '100%',
                paddingBottom: getTokenCssVar('SwanSemSpace4'),
            }}
        >
            <AlertBox skin="error" toast={false} accessibleTextForIcon={__('Error')}>
                <FlexBox alignItems="flex-start" justifyContent="space-between">
                    <Typography>{text}</Typography>
                    {canRetry && (
                        <Button skin="link" onClick={onRetry}>
                            <FlexBox alignItems="flex-start" gap="3">
                                <Box style={{ flexShrink: 0 }}>
                                    <Icon iconType="reset" size="small" />
                                </Box>
                                <Box>{__('Try again')}</Box>
                            </FlexBox>
                        </Button>
                    )}
                    {canReview && (
                        <Button skin="link" onClick={onReview}>
                            <FlexBox alignItems="flex-start" gap="3">
                                <Box style={{ flexShrink: 0 }}>
                                    <Icon iconType="customQuote" size="small" />
                                </Box>
                                <Box>{__('Review brief')}</Box>
                            </FlexBox>
                        </Button>
                    )}
                </FlexBox>
            </AlertBox>
        </Box>
    );
}
